import { createAction, props } from '@ngrx/store';
import { LocationCardData } from '../../models/location-card.model';
import { Customer } from 'app/shared/models/customer';
import { DiagnosticsState, LocationCardPermissions, LocationCardSettings, MonitorConfiguration } from '../reducers';
import { Locations } from 'app/shared/models/locations';
import { LocationCardMonitorSeries } from 'app/shared/models/monitorsetting';
import { AvailableDynamicSection, DynamicSection, SensorConfiguration } from '../models/dynamic-section-models';
import { LocationDetails } from 'app/shared/models/location-details';

export enum CoreActionType {
    // UI Actions
    CLOSE_DIALOG = '@form/CLOSE_DIALOG',
    PICK_MAP_COORDS = '@form/PICK_MAP_COORDS',
    SET_MAP_COORDS = '@form/SET_MAP_COORDS',
    OPEN_MODEM_PROVIDER_DIALOG = '@form/OPEN_MODEM_PROVIDER_DIALOG',
    SET_FROM_HOME_DASHBOARD = '@form/SET_FROM_HOME_DASHBOARD',
    OPEN_SILT_DIALOG = '@form/OPEN_SILT_DIALOG',
    CLOSE_SILT_DIALOG = '@form/CLOSE_SILT_DIALOG',

    // Navigation and business actions
    NAVIGATE_LOCATION_DASHBOARD = '@form/NAVIGATE_LOCATION_DASHBOARD',
    COLLECT_ACTION_CLICK = '@form/COLLECT_ACTION_CLICK',
    SCHEDULE_COLLECT = '@form/SCHEDULE_COLLECT',
    EXPORT_XML = '@form/EXPORT_XML',
    ACTIVATE_LOCATION = '@form/ACTIVATE_LOCATION',

    // Core action types
    SUBMIT_FORM = '@form/SUBMIT_FORM',
    INIT_STATE = '@form/INIT_STATE',
    INIT_SWITCH_STATE = '@form/INIT_SWITCH_STATE',
    SET_FORM_SETTINGS = '@form/SET_FORM_SETTINGS',
    SET_PERMISSIONS = '@form/SET_PERMISSIONS',

    // Customer & Location API actions
    LOAD_CUSTOMER = '@form/LOAD_CUSTOMER',
    LOAD_CUSTOMER_SUCCESS = '@form/LOAD_CUSTOMER_SUCCESS',
    LOAD_CUSTOMER_FAILURE = '@form/LOAD_CUSTOMER_FAILURE',

    LOAD_LOCATIONS = '@form/LOAD_LOCATIONS',
    LOAD_LOCATIONS_SUCCESS = '@form/LOAD_LOCATIONS_SUCCESS',
    LOAD_LOCATIONS_FAILURE = '@form/LOAD_LOCATIONS_FAILURE',

    LOAD_AVAILABLE_DYNAMIC_SECTIONS = '@form/LOAD_AVAILABLE_DYNAMIC_SECTIONS',
    LOAD_AVAILABLE_DYNAMIC_SECTIONS_SUCCESS = '@form/LOAD_AVAILABLE_DYNAMIC_SECTIONS_SUCCESS',
    PRELOAD_DYNAMIC_SECTIONS_SUCCESS = '@form/PRELOAD_DYNAMIC_SECTIONS_SUCCESS',
    LOAD_EXISTING_DYNAMIC_SECTIONS_SUCCESS = '@form/LOAD_EXISTING_DYNAMIC_SECTIONS_SUCCESS',

    LOAD_DYNAMIC_SECTIONS = '@form/LOAD_DYNAMIC_SECTIONS',
    LOAD_DYNAMIC_SECTIONS_SUCCESS = '@form/LOAD_DYNAMIC_SECTIONS_SUCCESS',

    LOAD_LATEST_SILT_VALUE = '@form/LOAD_LATEST_SILT_VALUE',

    TRANSITION_TO_EDIT_MODE = '@form/TRANSITION_TO_EDIT_MODE',
    TRANSITION_TO_EDIT_MODE_SUCCESS = '@form/TRANSITION_TO_EDIT_MODE_SUCCESS',
    LOAD_LOCATION_DETAILS_SUCCESS = '@form/LOAD_LOCATION_DETAILS_SUCCESS',
    LOAD_MONITOR_CONFIGURATION_SUCCESS = '@form/LOAD_MONITOR_CONFIGURATION_SUCCESS',
    LOAD_DIAGNOSTICS_SUCCESS = '@form/LOAD_DIAGNOSTICS_SUCCESS',
}

// UI Actions
export const closeDialog = createAction(CoreActionType.CLOSE_DIALOG);

export const pickMapCoords = createAction(CoreActionType.PICK_MAP_COORDS);

export const setMapCoords = createAction(
    CoreActionType.SET_MAP_COORDS,
    props<{ coords: { lat: number; lon: number } }>(),
);

export const openModemProviderDialog = createAction(CoreActionType.OPEN_MODEM_PROVIDER_DIALOG);

export const openLocationDashboard = createAction(CoreActionType.NAVIGATE_LOCATION_DASHBOARD);

export const collectLocation = createAction(CoreActionType.COLLECT_ACTION_CLICK);

export const scheduleCollect = createAction(CoreActionType.SCHEDULE_COLLECT);

export const exportXml = createAction(CoreActionType.EXPORT_XML);

export const activateLocation = createAction(CoreActionType.ACTIVATE_LOCATION);

// Application-specific Actions
export const submitForm = createAction(CoreActionType.SUBMIT_FORM, props<{ shouldClose: boolean; isActivationRequired: boolean }>());
export const initState = createAction(CoreActionType.INIT_STATE, props<{ data: LocationCardData }>());
export const initSwitchState = createAction(
    CoreActionType.INIT_SWITCH_STATE,
    props<{ customer: Customer; series: LocationCardMonitorSeries[] }>(),
);
export const setFormSettings = createAction(
    CoreActionType.SET_FORM_SETTINGS,
    props<{ settings: LocationCardSettings }>(),
);

export const setPermissions = createAction(
    CoreActionType.SET_PERMISSIONS,
    props<{ permissions: LocationCardPermissions }>(),
);

// Customer & Location API actions
export const loadCustomer = createAction(CoreActionType.LOAD_CUSTOMER, props<{ customerId: number }>());
export const loadCustomerSuccess = createAction(CoreActionType.LOAD_CUSTOMER_SUCCESS, props<{ customer: Customer }>());
export const loadCustomerFailure = createAction(CoreActionType.LOAD_CUSTOMER_FAILURE, props<{ error: unknown }>());

export const loadLocations = createAction(CoreActionType.LOAD_LOCATIONS, props<{ customerId: number }>());
export const loadLocationsSuccess = createAction(
    CoreActionType.LOAD_LOCATIONS_SUCCESS,
    props<{ locations: Locations[] }>(),
);
export const loadLocationsFailure = createAction(CoreActionType.LOAD_LOCATIONS_FAILURE, props<{ error: unknown }>());

export const loadAvailableDynamicSections = createAction(CoreActionType.LOAD_AVAILABLE_DYNAMIC_SECTIONS);
export const loadAvailableDynamicSectionsSuccess = createAction(
    CoreActionType.LOAD_AVAILABLE_DYNAMIC_SECTIONS_SUCCESS,
    props<{ data: AvailableDynamicSection[] }>(),
);

export const preloadDynamicSectionsSuccess = createAction(
    CoreActionType.PRELOAD_DYNAMIC_SECTIONS_SUCCESS,
    props<{ configTemplates: DynamicSection[] }>(),
);

export const loadExistingDynamicSectionsSuccess = createAction(
    CoreActionType.LOAD_EXISTING_DYNAMIC_SECTIONS_SUCCESS,
    props<{ data: SensorConfiguration[] }>(),
);

export const loadDynamicSections = createAction(CoreActionType.LOAD_DYNAMIC_SECTIONS, props<{ locationId: number }>());
export const loadDynamicSectionsSuccess = createAction(CoreActionType.LOAD_DYNAMIC_SECTIONS_SUCCESS);

export const setFromHomeDashboard = createAction(CoreActionType.SET_FROM_HOME_DASHBOARD, props<{ fromHomeDashboard: boolean }>());

export const openSiltDialog = createAction(CoreActionType.OPEN_SILT_DIALOG, props<{ updateFieldId: string; mpIndex: 0 | 1 }>());
export const closeSiltDialog = createAction(CoreActionType.CLOSE_SILT_DIALOG, props<{ lastEditedValue: number | null; updateFieldId: string; }>());

export const loadLatestSiltValue = createAction(CoreActionType.LOAD_LATEST_SILT_VALUE);

export const transitionToEditMode = createAction(
    CoreActionType.TRANSITION_TO_EDIT_MODE,
    props<{ locationId: number; customerId: number }>()
);

export const transitionToEditModeSuccess = createAction(CoreActionType.TRANSITION_TO_EDIT_MODE_SUCCESS);

export const loadLocationDetailsSuccess = createAction(
    CoreActionType.LOAD_LOCATION_DETAILS_SUCCESS,
    props<{ details: LocationDetails[] }>()
);

export const loadMonitorConfigurationSuccess = createAction(
    CoreActionType.LOAD_MONITOR_CONFIGURATION_SUCCESS,
    props<{ config: MonitorConfiguration }>()
);

export const loadDiagnosticsSuccess = createAction(
    CoreActionType.LOAD_DIAGNOSTICS_SUCCESS,
    props<DiagnosticsState>()
);